/**
 * Project List View - Filterable task list
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchTasks, fetchTask, updateTask } from '../store/taskSlice';
import { useSearchParams } from 'react-router-dom';
// import MultiSelectDropdown from '@/components/MultiSelectDropdown';
import AssigneeChips from '@/components/AssigneeChips';
import AvatarTooltip from '@/components/AvatarTooltip';
import { projectMemberAPI } from '@/api/projectMemberAPI';
import { Filter, User } from 'lucide-react';
import FilterDrawer from '@/components/FilterDrawer';
import { sprintAPI } from '@/api/sprintAPI';
import { userAPI } from '@/api/userAPI';
import { format } from 'date-fns';
import { MessageSquare } from 'lucide-react';
import { TaskModal } from '@/components/TaskModal/TaskModal';
import { commentAPI } from '@/api/commentAPI';
import { deleteComment, formatCommentsForActivity, updateComment } from '@/utils/commentUtils';
import { ensureHtml, toPlainText } from '@/utils/richText';
import { CommentActivity, Comment } from '@/types/comments';
import { Task } from '@/types';
import SubtaskModal from '@/components/SubtaskModal/SubtaskModal';
import type { Subtask } from '@/types';
import { updateSubtask } from '../store/taskSlice';
import { mastersAPI, WorkflowStatus, TaskType, Priority } from '@/api/mastersApi';
import { Loader } from '@/components/Loader';
import { API_BASE_URL } from '@/utils/constants';
import { subtaskAPI } from '@/api/subtaskAPI';
import TaskTypeIcon from '@/components/TaskTypeIcon';
import { getTasksDueWithin7Days } from '@/utils/daysCalculator';

// Full, validated ProjectList component
export const ProjectList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { tasks, loading, currentTask } = useAppSelector((s) => s.tasks);
  const { currentProject } = useAppSelector((s) => s.projects);
  const authUserId = useAppSelector((s) => s.auth.user?.id);
  const [searchParams] = useSearchParams();

  // UI / filter state
  const [search, setSearch] = useState('');
  const [projectMembers, setProjectMembers] = useState<any[]>([]);
  const [usersById, setUsersById] = useState<Record<string, any>>({});
  const [selectedPersons, setSelectedPersons] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set());
  const [workflowStatuses, setWorkflowStatuses] = useState<WorkflowStatus[]>([]);
  const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
  const [priorities, setPriorities] = useState<Priority[]>([]);
  const [mastersLoaded, setMastersLoaded] = useState(false);
  const [sprintsLoaded, setSprintsLoaded] = useState(false);
  const [membersLoaded, setMembersLoaded] = useState(false);
  const [bootLoading, setBootLoading] = useState(true);

  // Filter tooltip state
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterBtnWrapRef = useRef<HTMLDivElement | null>(null);
  const [fpActiveSprints, setFpActiveSprints] = useState(false);
  const [fpAssignToMe, setFpAssignToMe] = useState(false);
  const [fpDoneItems, setFpDoneItems] = useState(false); // when true => show only done/completed
  const [fpCreatedFrom, setFpCreatedFrom] = useState<string | null>(null);
  const [fpCreatedTo, setFpCreatedTo] = useState<string | null>(null);
  const [fpDateError, setFpDateError] = useState<string | null>(null);

  // Group tooltip
  const [isGroupOpen, setIsGroupOpen] = useState(false);
  const [groupBy, setGroupBy] = useState<'none' | 'status' | 'sprint' | 'assignee'>('none');


  // Task modal
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  // related work removed from this view
  const [activity, setActivity] = useState<CommentActivity[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);

  // Subtask Modal States
  const [activeSubtask, setActiveSubtask] = useState<Subtask | null>(null);
  const [isSubtaskModalOpen, setIsSubtaskModalOpen] = useState(false);
  const [parentTaskForSubtask, setParentTaskForSubtask] = useState<Task | null>(null);
  // Pagination
  const [page, setPage] = useState(1);
  const pageSize = 10;
  // Inline estimate editing
  const [editingEstimateId, setEditingEstimateId] = useState<string | null>(null);
  const [estimateDraft, setEstimateDraft] = useState<string>('');

  const beginEditEstimate = (task: any) => {
    setEditingEstimateId(String(task.id));
    const current = (task as any).estimate ?? (task as any).story_points ?? '';
    setEstimateDraft(current === null || current === undefined ? '' : String(current));
  };

  const commitEstimate = async (taskId: string) => {
    try {
      const val = estimateDraft.trim();
      const num = val === '' ? null : Number(val);
      const payload: any = { estimate: num };
      await dispatch(updateTask({ id: taskId, data: payload })).unwrap();
      if (currentProject?.id) dispatch(fetchTasks({ project_id: currentProject.id }));
    } catch {
      // ignore
    } finally {
      setEditingEstimateId(null);
    }
  };

  // helpers
  const projectKey = currentProject?.key || 'AHM';
  const statusFilter = searchParams.get('status');
  const priorityFilter = searchParams.get('priority');
  const taskTypeFilter = searchParams.get('task_type');
  const updatedAfterFilter = searchParams.get('updated_after');
  const dueSoon=searchParams.get('dueSoon');


  // Apply URL filters from Summary page on load and when query changes
  useEffect(() => {
    const normalize = (s?: string) => String(s || '').toLowerCase();
    const compact = (s?: string) => normalize(s).replace(/[^a-z]+/g, '');

    // Reset quick filters relevant to URL params before applying
    setFpDoneItems(false);
    setSelectedStatuses([]);
    setSelectedTypes([]);

    if (statusFilter) {
      const s = normalize(statusFilter);
      // Treat completed/done as the Done quick filter (covers multiple done-like statuses)
      if (s === 'completed' || s === 'done') {
        setFpDoneItems(true);
      } else {
        // Try to map incoming status to an existing task status by fuzzy match
        const filterComp = compact(s);
        const taskStatuses = Array.from(new Set((tasks || []).map((t: any) => normalize(t.status)).filter(Boolean)));
        const matchesExact = taskStatuses.find(ts => ts === s);
        if (matchesExact) {
          setSelectedStatuses([matchesExact]);
        } else {
          const withScores = taskStatuses.map(ts => ({
            ts,
            comp: compact(ts)
          }));
          // Prefer contains or equality in either direction
          const contains = withScores.find(w => w.comp === filterComp || w.comp.includes(filterComp) || filterComp.includes(w.comp));
          if (contains) {
            setSelectedStatuses([contains.ts]);
          } else {
            // Fallback: keep the incoming value
            setSelectedStatuses([s]);
          }
        }
      }
    }

    if (taskTypeFilter) {
      setSelectedTypes([String(taskTypeFilter).toLowerCase()]);
    }
  }, [statusFilter, taskTypeFilter, tasks]);
  const currentUserId = String(authUserId ?? '');

  // load master statuses/types
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [statuses, types, prios] = await Promise.all([
          mastersAPI.getWorkflowStatuses(),
          mastersAPI.getTaskTypes(),
          mastersAPI.getPriorities()
        ]);
        if (!mounted) return;
        setWorkflowStatuses(statuses);
        setTaskTypes(types);
        setPriorities(prios);
        setMastersLoaded(true);
      } catch {
        // ensure boot flow doesn't hang
        setMastersLoaded(true);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Aggregate boot loading similar to Kanban/Summary (wait for tasks + masters + sprints + members)
  useEffect(() => {
    if (!loading && mastersLoaded && sprintsLoaded && membersLoaded) setBootLoading(false);
    else setBootLoading(true);
  }, [loading, mastersLoaded, sprintsLoaded, membersLoaded]);

  // tooltip refs for outside click
  const filterRef = useRef<HTMLDivElement | null>(null);
  const groupRef = useRef<HTMLDivElement | null>(null);

  // close tooltips on outside click / Escape
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (isFilterOpen) {
        if (filterRef.current && !filterRef.current.contains(target)) {
          const path = (e as any).composedPath ? (e as any).composedPath() : (e as any).path || [];
          const clickedTrigger = path.some((n: any) => n?.classList?.contains?.('filter-trigger'));
          if (!clickedTrigger) setIsFilterOpen(false);
        }
      }
      if (isGroupOpen) {
        if (groupRef.current && !groupRef.current.contains(target)) {
          const path = (e as any).composedPath ? (e as any).composedPath() : (e as any).path || [];
          const clickedTrigger = path.some((n: any) => n?.classList?.contains?.('group-trigger'));
          if (!clickedTrigger) setIsGroupOpen(false);
        }
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setIsFilterOpen(false);
        setIsGroupOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isFilterOpen, isGroupOpen]);

  // fetch tasks on project change
  useEffect(() => {
    if (currentProject?.id) dispatch(fetchTasks({ project_id: currentProject.id }));
  }, [dispatch, currentProject]);

  // Fetch sprints for FilterDrawer
  const [sprints, setSprints] = useState<any[]>([]);
  useEffect(() => {
    (async () => {
      if (!currentProject?.id) return;
      try {
        const data = await sprintAPI.getSprints(currentProject.id);
        setSprints(data || []);
        setSprintsLoaded(true);
      } catch {
        setSprintsLoaded(true);
      }
    })();
  }, [currentProject?.id]);

  // Drawer filter states (id-based like Board/Backlog)
  const [selectedStatusIds, setSelectedStatusIds] = useState<string[]>([]);
  const [selectedPriorityIds, setSelectedPriorityIds] = useState<string[]>([]);
  const [selectedTaskTypeIds, setSelectedTaskTypeIds] = useState<string[]>([]);
  const [selectedSprintIds, setSelectedSprintIds] = useState<string[]>([]);
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [parentOnly, setParentOnly] = useState<boolean>(false);
  const [subtaskOnly, setSubtaskOnly] = useState<boolean>(false);

  // load project members for chips overflow + merge with user lookups
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!currentProject?.id) return;
      try {
        const data = await projectMemberAPI.getProjectMembers(currentProject.id);
        if (!mounted) return;
        setProjectMembers(data || []);
        setMembersLoaded(true);
      } catch {
        // ignore
        setMembersLoaded(true);
      }
    })();
    return () => { mounted = false; };
  }, [currentProject?.id]);

  // resolve users
  useEffect(() => {
    let mounted = true;
    (async () => {
      const ids = Array.from(new Set((tasks || []).map((t: any) => t?.assigned_to).filter(Boolean).map(String)));
      if (ids.length === 0) {
        if (mounted) setUsersById({});
        return;
      }
      // Use functional update to avoid dependency on usersById
      setUsersById(prev => {
        const toFetch = ids.filter((id) => !prev[id] && !projectMembers.some(m => String(m.user_id ?? m._id) === id));
        if (toFetch.length === 0) return prev;

        // Fetch users asynchronously
        (async () => {
          try {
            const fetched = await Promise.all(toFetch.map(async (id) => {
              try {
                const u = await userAPI.getUser(id);
                return [id, u] as const;
              } catch {
                return [id, { id, username: `User ${id}` }] as const;
              }
            }));
            if (!mounted) return;
            setUsersById(current => {
              const updated = { ...current };
              fetched.forEach(([id, u]) => { updated[id] = u; });
              return updated;
            });
          } catch {
            // ignore
          }
        })();

        return prev;
      });
    })();
    return () => { mounted = false; };
  }, [tasks, projectMembers]);

  // persons for dropdowns
  const persons = useMemo(() => {
    const byId: Record<string, any> = {};
    projectMembers.forEach((m: any) => {
      const key = String(m.user_id ?? m._id ?? '');
      if (key) byId[key] = m;
    });
    Object.entries(usersById).forEach(([id, u]) => { if (!byId[id]) byId[id] = u; });
    return Object.entries(byId).map(([id, u]) => ({
      user_id: id,
      user_display_name: (u as any).user_display_name || (u as any).display_name || (u as any).full_name || (u as any).username || (u as any).email || id,
      user_email: (u as any).email,
      username: (u as any).username,
      _raw: u
    }));
  }, [projectMembers, usersById]);

  const toInitials = (display?: string) => {
    if (!display) return '';
    const parts = display.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  };
  const colorFromSeed = (seed: string) => {
    const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500', 'bg-red-500', 'bg-yellow-500', 'bg-cyan-500'];
    const sum = Array.from(seed).reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
    return colors[sum % colors.length];
  };

  const userAvatars = useMemo(() => {
    const set = new Set<string>();
    (tasks || []).forEach((t: any) => { if (t.assigned_to) set.add(String(t.assigned_to)); });
    return Array.from(set).map(uid => {
      const m = persons.find(p => String(p.user_id) === String(uid) || String((p as any)._id) === String(uid));
      const display = m?.user_display_name || m?.username || m?.user_email || 'User';
      return { id: String(uid), initials: toInitials(display), color: colorFromSeed(String(uid) || display), title: display };
    });
  }, [tasks, persons]);

  // Compute labels for filter drawer
  const allLabels = useMemo(() => {
    const set = new Set<string>();
    (tasks || []).forEach((t: any) => {
      const raw = (t?.labels ?? []) as any;
      const arr = Array.isArray(raw)
        ? raw
        : typeof raw === 'string'
          ? raw.split(',').map((l: string) => l.trim()).filter(Boolean)
          : [];
      arr.forEach((l: string) => set.add(l));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [tasks]);

  const formatDisplay = (str?: string) => {
    if (!str) return '';
    return str.split('_').map(w => w[0]?.toUpperCase() + w.slice(1)).join(' ');
  };

  const types = useMemo(() => {
    const fromMasters = taskTypes.map(tt => ({ value: String(tt.name).toLowerCase(), label: tt.name }));
    if (fromMasters.length > 0) return fromMasters;
    return Array.from(new Set((tasks || []).map((t: any) => String(t.task_type || '').toLowerCase()).filter(Boolean)))
      .map((t) => ({ value: t, label: formatDisplay(t) }));
  }, [taskTypes, tasks]);

  const statuses = useMemo(() => {
    const fromMasters = workflowStatuses.map(ws => ({ value: String(ws.name).toLowerCase(), label: ws.name }));
    if (fromMasters.length > 0) return fromMasters;
    return Array.from(new Set((tasks || []).map((t: any) => String(t.status || '').toLowerCase()).filter(Boolean)))
      .map((s) => ({ value: s, label: formatDisplay(s) }));
  }, [workflowStatuses, tasks]);

  // Count of active drawer filters for badge
  const activeFilterCount = useMemo(() => {
    const bools = (parentOnly ? 1 : 0) + (subtaskOnly ? 1 : 0);
    const sums = selectedStatusIds.length + selectedPriorityIds.length + selectedTaskTypeIds.length + selectedPersons.length + selectedSprintIds.length + selectedLabels.length;
    return bools + sums;
  }, [parentOnly, subtaskOnly, selectedStatusIds.length, selectedPriorityIds.length, selectedTaskTypeIds.length, selectedPersons.length, selectedSprintIds.length, selectedLabels.length]);

  // Render task type image from API (masters) with fallbacks
  const resolveTaskTypeIconUrl = (raw?: string | null) => {
    if (!raw) return null;
    const builtInIcons = ['task', 'bug', 'story', 'epic', 'subtask'];
    if (builtInIcons.includes(raw.toLowerCase())) return null;
    try {
      if (raw.startsWith('data:') || raw.startsWith('http://') || raw.startsWith('https://')) return raw;
      const api = new URL(API_BASE_URL);
      const origin = `${api.protocol}//${api.host}`;
      const path = raw.startsWith('/') ? raw : `/${raw}`;
      return origin + path;
    } catch {
      return null;
    }
  };

  const taskTypeById = useMemo(() => {
    const map = new Map<string, TaskType>();
    taskTypes.forEach(tt => { if (tt?.id) map.set(String(tt.id), tt); });
    return map;
  }, [taskTypes]);

  const taskTypeByName = useMemo(() => {
    const map = new Map<string, TaskType>();
    taskTypes.forEach(tt => { if (tt?.name) map.set(String(tt.name).toLowerCase(), tt); });
    return map;
  }, [taskTypes]);

  const renderTaskType = (task: any) => {
    const id = (task as any).task_type_id;
    const nameFromTask = (task as any).task_type ? String((task as any).task_type).toLowerCase() : '';
    const match = (Array.isArray(taskTypes) ? taskTypes : []).find(tt => {
      if (id && String(tt.id) === String(id)) return true;
      const n = (tt.name || '').toLowerCase();
      return nameFromTask && n === nameFromTask;
    });
    const iconRaw = (match as any)?.icon || (match as any)?.icon_url || (match as any)?.image || (match as any)?.image_url;
    const iconUrl = resolveTaskTypeIconUrl(iconRaw);
    const n = (match?.name || nameFromTask || 'task').toLowerCase();
    return <TaskTypeIcon url={iconUrl} name={n} className="w-4 h-4" />;
  };

  const getStatusColor = (status = '') => {
    const map: Record<string, string> = {
      backlog: 'bg-gray-100 text-gray-800',
      todo: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      done: 'bg-green-100 text-green-800',
      qa_in_progress: 'bg-purple-100 text-purple-800',
      requirements: 'bg-orange-100 text-orange-800'
    };
    return map[status] || 'bg-gray-100 text-gray-800';
  };

  // getTaskStatusName declared below (single definition)

  const getTaskTypeName = (t: any): string => {
    const normalized = (s?: string) => String(s || '').toLowerCase();
    const byId = taskTypes.find(tt => tt.id === t?.task_type_id)?.name || '';
    return normalized(t?.task_type) || normalized(byId);
  };

  const getTaskPriorityKey = (t: any): string => {
    const p = priorities.find(pr => pr.id === t?.priority_id);
    const key = (p as any)?.key || p?.name?.toLowerCase() || '';
    return String(key).toLowerCase();
  };

  const getAssigneeName = (assigneeId?: string, task?: any) => {
    if (!assigneeId) return 'Unassigned';
    const assigneeIdStr = String(assigneeId);

    // First, try to find in persons array (merged projectMembers + usersById)
    const person = persons.find(p => String(p.user_id) === assigneeIdStr);
    if (person) {
      return person.user_display_name || person.username || person.user_email || 'User';
    }

    // Fallback: try usersById directly
    const user = usersById[assigneeIdStr];
    if (user) {
      return (user as any).user_display_name || (user as any).display_name || (user as any).username || (user as any).email || 'User';
    }

    // Fallback: use assigned_user_name from task if available (from API)
    if (task?.assigned_user_name) {
      return task.assigned_user_name;
    }

    return 'Unknown';
  };
  const getAssigneeInitials = (assigneeId?: string, task?: any) => {
    if (!assigneeId) return 'U';
    const assigneeIdStr = String(assigneeId);

    // First, try to find in persons array
    const person = persons.find(p => String(p.user_id) === assigneeIdStr);
    if (person) {
      const name = person.user_display_name || person.username || person.user_email || '';
      return toInitials(name);
    }

    // Fallback: try usersById
    const user = usersById[assigneeIdStr];
    if (user) {
      const name = (user as any).user_display_name || (user as any).display_name || (user as any).username || (user as any).email || '';
      return toInitials(name);
    }

    // Fallback: use assigned_user_name from task
    if (task?.assigned_user_name) {
      return toInitials(task.assigned_user_name);
    }

    return 'U';
  };

  const getTaskStatusName = (t: any): string => {
    const normalized = (s?: string) => String(s || '').toLowerCase();
    const byId = workflowStatuses.find(ws => ws.id === t?.workflow_status_id)?.name || '';
    return normalized(t?.status) || normalized(byId);
  };

  // sprints list (for dropdowns) — retained for future use
  // const sprints = useMemo(() => {
  //   const ids = Array.from(new Set((tasks || []).map((t: any) => t.sprint_id).filter((s: any) => s !== null && s !== undefined)));
  //   return ids.map((s: any) => ({ value: String(s), label: `Sprint ${s}` }));
  // }, [tasks]);

  // filtering
  const filteredTasks = useMemo(() => {
    const normalize = (s?: string) => String(s || '').toLowerCase();
    const compact = (s?: string) => normalize(s).replace(/[^a-z]+/g, ''); // remove non-letters for fuzzy status match

    let result = (tasks || []).filter((task: any) => {
      if (search && !(task.title || '').toLowerCase().includes(search.toLowerCase())) return false;
      const byId = (v?: string) => (v ? String(v) : '');

      // Assignee from chips/drawer
      if (selectedPersons.length > 0 && !selectedPersons.includes(String(task.assigned_to))) return false;

      // Type filter: case-insensitive
      if (selectedTypes.length > 0 && !selectedTypes.includes(getTaskTypeName(task))) return false;
      if (selectedTaskTypeIds.length > 0 && !selectedTaskTypeIds.includes(byId(task.task_type_id))) return false;

      // Status filter: support exact and fuzzy match (e.g., dev_in_progress vs in_progress)
      if (selectedStatuses.length > 0) {
        const taskStatus = getTaskStatusName(task);
        const taskStatusCompacted = compact(taskStatus);
        const match = selectedStatuses.some((s) => s === taskStatus || compact(s) === taskStatusCompacted);
        if (!match) return false;
      }
      if (selectedStatusIds.length > 0) {
        const sid = byId(task.workflow_status_id);
        if (sid) {
          if (!selectedStatusIds.includes(sid)) return false;
        } else if (task.status) {
          const legacy = String(task.status).toLowerCase();
          const nameMatch = workflowStatuses.find((s) => selectedStatusIds.includes(s.id) && (s.name || '').toLowerCase().includes(legacy));
          if (!nameMatch) return false;
        } else {
          return false;
        }
      }

      if (fpActiveSprints && !(task.sprint_id !== null && task.sprint_id !== undefined && String(task.sprint_id) !== '')) return false;
      if (fpAssignToMe && currentUserId) {
        if (!task.assigned_to || String(task.assigned_to) !== currentUserId) return false;
      }

      if (fpDoneItems) {
        const doneSet = new Set(['done', 'completed']);
        if (!doneSet.has(getTaskStatusName(task))) return false;
      }

      // Drawer sprints/priorities/labels/parent-subtask
      if (selectedSprintIds.length > 0 && !selectedSprintIds.includes(byId(task.sprint_id))) return false;
      if (selectedPriorityIds.length > 0 && !selectedPriorityIds.includes(byId(task.priority_id))) return false;
      if (parentOnly && task.parent_id) return false;
      if (subtaskOnly && !task.parent_id) return false;
      if (selectedLabels.length > 0) {
        const labelsArr = Array.isArray(task?.labels)
          ? task.labels
          : typeof task?.labels === 'string'
            ? task.labels.split(',').map((l: string) => l.trim()).filter(Boolean)
            : [];
        const hasAny = labelsArr.some((l: string) => selectedLabels.includes(l));
        if (!hasAny) return false;
      }

      if (fpCreatedFrom) {
        const from = new Date(fpCreatedFrom);
        const created = task.created_at ? new Date(task.created_at) : null;
        if (!created || created < from) return false;
      }
      if (fpCreatedTo) {
        const to = new Date(fpCreatedTo);
        const created = task.created_at ? new Date(task.created_at) : null;
        if (!created || created > to) return false;
      }

      // Apply updated_after URL filter if present (from Summary "Updated" card)
      if (updatedAfterFilter) {
        const threshold = new Date(String(updatedAfterFilter));
        if (!isNaN(threshold.getTime())) {
          const updated = task.updated_at ? new Date(task.updated_at) : (task.created_at ? new Date(task.created_at) : null);
          if (!updated || updated < threshold) return false;
        }
      }

      // Apply priority URL filter if present (from Summary priority breakdown)
      if (priorityFilter) {
        const pf = String(priorityFilter).toLowerCase();
        if (getTaskPriorityKey(task) !== pf) return false;
      }

      return true;
    });

    if (dueSoon === 'true') {
      result = getTasksDueWithin7Days(result as any[]);
    }

    return result;

  }, [tasks, search, selectedPersons, selectedTypes, selectedStatuses, selectedStatusIds, selectedTaskTypeIds, selectedSprintIds, selectedPriorityIds, selectedLabels, parentOnly, subtaskOnly, fpActiveSprints, fpAssignToMe, fpDoneItems, fpCreatedFrom, fpCreatedTo, currentUserId, updatedAfterFilter, priorityFilter, dueSoon, workflowStatuses, taskTypes, priorities]);
  // Reset to first page when filters change
  useEffect(() => {
    setPage(1);
  }, [search, selectedPersons, selectedTypes, selectedStatuses, selectedStatusIds, selectedTaskTypeIds, selectedSprintIds, selectedPriorityIds, selectedLabels, parentOnly, subtaskOnly, fpActiveSprints, fpAssignToMe, fpDoneItems, fpCreatedFrom, fpCreatedTo, updatedAfterFilter, priorityFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredTasks.length / pageSize));
  const pageStart = (page - 1) * pageSize;
  const pageEnd = Math.min(filteredTasks.length, pageStart + pageSize);
  const currentPageTasks = filteredTasks.slice(pageStart, pageEnd);

  // Compute internal scroll container height so page scroll can be disabled
  // Compute internal scroll container height so page scroll can be disabled
  const scrollWrapRef = useRef<HTMLDivElement | null>(null);
  const [scrollHeight, setScrollHeight] = useState<number | null>(null);
  useEffect(() => {
    const recompute = () => {
      if (!scrollWrapRef.current) return;
      const rect = scrollWrapRef.current.getBoundingClientRect();
      const padding = 8;
      // More accurate calculation that accounts for footer space
      const headerHeight = 200; // Approximate height of header + filters
      const footerHeight = 60; // Approximate height of pagination
      const h = Math.max(window.innerHeight - rect.top - headerHeight - footerHeight - padding, 240);
      setScrollHeight(h);
    };

    recompute();
    // Add a small delay to ensure DOM is fully rendered
    const timeoutId = setTimeout(recompute, 100);

    window.addEventListener('resize', recompute);
    window.addEventListener('orientationchange', recompute);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', recompute);
      window.removeEventListener('orientationchange', recompute);
    };
  }, []);

  // grouping
  const groupedTasks = useMemo(() => {
    if (groupBy === 'none') return null;
    const map = new Map<string, any[]>();
    (filteredTasks || []).forEach((t: any) => {
      let key = 'Unspecified';
      if (groupBy === 'status') key = getTaskStatusName(t) || 'Unspecified';
      if (groupBy === 'sprint') key = t.sprint_id ? `Sprint ${t.sprint_id}` : 'No sprint';
      if (groupBy === 'assignee') key = t.assigned_to ? getAssigneeName(String(t.assigned_to), t) : 'Unassigned';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(t);
    });
    return map;
  }, [filteredTasks, groupBy, persons]);

  // selection helpers
  const handleTaskSelection = (taskId: string, checked: boolean) => {
    setSelectedTasks(prev => {
      const next = new Set(prev);
      if (checked) next.add(taskId); else next.delete(taskId);
      return next;
    });
  };
  const handleSelectAll = () => {
    if (selectedTasks.size === filteredTasks.length) setSelectedTasks(new Set());
    else setSelectedTasks(new Set(filteredTasks.map((t: any) => t.id)));
  };

  // task modal flow
  const loadComments = async (taskId: string) => {
    try {
      const c = await commentAPI.getComments(taskId);
      setComments(c);
      setActivity(formatCommentsForActivity(c));
    } catch {
      setComments([]);
      setActivity([]);
    }
  };

  // related work stub removed

  const handleTaskClick = async (task: Task) => {
    setSelectedTask(task);
    setIsTaskModalOpen(true);

    // Ensure project members are loaded/refreshed when opening modal
    if (currentProject?.id) {
      try {
        const data = await projectMemberAPI.getProjectMembers(currentProject.id);
        setProjectMembers(data || []);
      } catch (error) {
        console.error('Failed to refresh project members:', error);
      }
    }

    try {
      await Promise.all([dispatch(fetchTask(task.id)), loadComments(task.id)]);
    } catch (e) {
      console.error(e);
    }
  };
  const handleTaskModalClose = () => {
    setIsTaskModalOpen(false);
    setSelectedTask(null);
    setActivity([]);
    setComments([]);
  };

  const openSubtaskModal = (subtask: Subtask, parent: Task) => {
    setParentTaskForSubtask(parent);
    setIsTaskModalOpen(false);
    setActiveSubtask(subtask);
    setIsSubtaskModalOpen(true);
  };

  const closeSubtaskModal = () => {
    setIsSubtaskModalOpen(false);
    setActiveSubtask(null);
    setParentTaskForSubtask(null);
  };

  // Sync selectedTask with updated task from Redux state after status update (following BacklogView pattern)
  useEffect(() => {
    if (selectedTask && currentTask && currentTask.id === selectedTask.id) {
      setSelectedTask(currentTask);
    }
  }, [currentTask, selectedTask?.id]);

  // Following BacklogView pattern - newStatus is already the workflow_status_id
  const handleStatusUpdate = async (taskId: string, workflowStatusId: string) => {
    try {
      await dispatch(updateTask({
        id: taskId,
        data: { workflow_status_id: workflowStatusId || undefined }
      })).unwrap();

      if (currentProject?.id) {
        dispatch(fetchTasks({ project_id: currentProject.id }));
        // Update selectedTask to reflect the status change
        if (selectedTask?.id === taskId) {
          dispatch(fetchTask(taskId));
        }
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  // Date validation + clear
  const applyFilterModal = () => {
    setFpDateError(null);
    if (fpCreatedFrom && fpCreatedTo) {
      const from = new Date(fpCreatedFrom);
      const to = new Date(fpCreatedTo);
      if (isNaN(from.getTime()) || isNaN(to.getTime())) {
        setFpDateError('Invalid date(s)');
        return false;
      }
      if (from > to) {
        setFpDateError('From date must be before or equal to To date');
        return false;
      }
    }
    return true;
  };
  const handleClearFilters = () => {
    setFpActiveSprints(false);
    setFpAssignToMe(false);
    setFpDoneItems(false);
    setFpCreatedFrom(null);
    setFpCreatedTo(null);
    setFpDateError(null);
    setSelectedTypes([]);
    setSelectedStatuses([]);
  };

  const formatDateSafe = (d?: string) => {
    try { return d ? format(new Date(d), 'dd MMM yyyy') : '-'; } catch { return '-'; }
  };

  if (bootLoading || loading) {
    return (
      <Loader variant="overlay" size="xl" text="Loading list..." />
    );
  }

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search List" className="border p-2 rounded-lg w-64 border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" />

          <AssigneeChips
            items={persons.map(p => ({ id: String(p.user_id), name: p.user_display_name || p.username || p.user_email || 'User' }))}
            selectedIds={selectedPersons}
            onToggle={(id) => setSelectedPersons(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
          />

          {/* Filter button + drawer like Board/Backlog */}
          <div ref={filterBtnWrapRef} className="relative">
            <button onClick={() => setIsFilterOpen((v) => !v)} className="h-8 px-3 rounded-md border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 flex items-center gap-2 relative">
              <Filter className="w-4 h-4" />
              Filter
              {activeFilterCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center text-[10px] leading-none font-semibold bg-blue-600 text-white rounded-full px-1.5 py-0.5">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {isFilterOpen && (
              <FilterDrawer
                isOpen={isFilterOpen}
                anchorRef={filterBtnWrapRef}
                onClose={() => setIsFilterOpen(false)}
                workflowStatuses={workflowStatuses}
                taskTypes={taskTypes}
                priorities={priorities}
                sprints={sprints as any}
                members={persons as any}
                allLabels={allLabels}
                selectedStatusIds={selectedStatusIds} setSelectedStatusIds={setSelectedStatusIds}
                selectedPriorityIds={selectedPriorityIds} setSelectedPriorityIds={setSelectedPriorityIds}
                selectedTaskTypeIds={selectedTaskTypeIds} setSelectedTaskTypeIds={setSelectedTaskTypeIds}
                selectedSprintIds={selectedSprintIds} setSelectedSprintIds={setSelectedSprintIds}
                selectedAssigneeIds={selectedPersons} setSelectedAssigneeIds={setSelectedPersons}
                selectedLabels={selectedLabels} setSelectedLabels={setSelectedLabels}
                parentOnly={parentOnly} setParentOnly={setParentOnly}
                subtaskOnly={subtaskOnly} setSubtaskOnly={setSubtaskOnly}
              />
            )}
          </div>
        </div>

        {/* Group trigger on right */}
        <div className="flex items-center gap-2">
          <div className="relative">
            <button onClick={() => setIsGroupOpen(v => !v)} className="group-trigger flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
              {groupBy !== 'none' ? `Grouped: ${formatDisplay(groupBy)}` : 'Group'}
            </button>

            {isGroupOpen && (
              <div ref={groupRef} className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-lg p-4 w-64 z-50 border border-gray-200">
                <div className="space-y-3">
                  <div className="text-sm font-medium">{groupBy !== 'none' ? `Grouped By: ${formatDisplay(groupBy)}` : 'Group'}</div>
                  <label className="flex items-center gap-2"><input type="radio" name="group" checked={groupBy === 'none'} onChange={() => setGroupBy('none')} /><span className="text-sm">None</span></label>
                  <label className="flex items-center gap-2"><input type="radio" name="group" checked={groupBy === 'status'} onChange={() => setGroupBy('status')} /><span className="text-sm">Status</span></label>
                  <label className="flex items-center gap-2"><input type="radio" name="group" checked={groupBy === 'sprint'} onChange={() => setGroupBy('sprint')} /><span className="text-sm">Sprint</span></label>
                  <label className="flex items-center gap-2"><input type="radio" name="group" checked={groupBy === 'assignee'} onChange={() => setGroupBy('assignee')} /><span className="text-sm">Assignee</span></label>
                </div>

                <div className="mt-4 flex justify-between gap-2">
                  <button onClick={() => setGroupBy('none')} className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 text-red-600">Clear</button>
                  <button onClick={() => setIsGroupOpen(false)} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded">Done</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table container: horizontal + vertical scroll inside only */}
      <div className="bg-white rounded-lg border border-gray-200 max-w-full min-w-0">
        <div
          ref={scrollWrapRef}
          className="overflow-x-auto w-full min-w-0"
          style={{
            height: scrollHeight ? `${scrollHeight}px` : undefined,
            maxHeight: 'calc(100vh - 280px)', // Added maxHeight as fallback
            overflowY: 'auto',
            scrollbarGutter: 'stable both-edges' as any,
            overscrollBehavior: 'contain' as any
          }}
        >
          <table className="w-max min-w-[1400px] table-auto">
            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10"> {/* Added sticky header */}
              <tr>
                {/* <th className="px-4 py-3 text-left">
            <input type="checkbox" checked={selectedTasks.size === filteredTasks.length && filteredTasks.length > 0} onChange={handleSelectAll} className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500" />
          </th> */}
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Type</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Key</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Task</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Status</th>
                {/* <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Comments</th> */}
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Sprint</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Assignee</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Due date</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Labels</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Created</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Updated</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {groupedTasks ? (
                Array.from(groupedTasks.entries()).map(([groupTitle, rows]) => (
                  <React.Fragment key={groupTitle}>
                    <tr className="bg-gray-100">
                      <td colSpan={11} className="px-4 py-2 text-sm font-medium">{groupTitle} ({rows.length})</td>
                    </tr>
                    {rows.map((task: any) => (
                      <tr key={task.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleTaskClick(task)}>
                        {/* <td className="px-4 py-3">
                          <input type="checkbox" checked={selectedTasks.has(task.id)} onChange={(e) => { e.stopPropagation(); handleTaskSelection(task.id, e.target.checked); }} className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500" />
                        </td> */}
                        <td className="px-4 py-3">{renderTaskType(task)}</td>
                        <td className="px-4 py-3"><div className="text-sm font-medium text-blue-600">{projectKey}-{task.display_number}</div></td>
                        <td className="px-4 py-3"><div className="text-sm text-gray-900">{task.title}</div></td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${getStatusColor(getTaskStatusName(task))}`}>
                            {String(getTaskStatusName(task)).toUpperCase()}
                          </span>
                        </td>
                        {/* <td className="px-4 py-3"><div className="flex items-center gap-1 text-sm text-gray-500"><MessageSquare className="w-4 h-4" /><span>{(task.comments || 0)} comment{(task.comments !== 1) ? 's' : ''}</span></div></td> */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {task.assigned_to ? (
                              <>
                                <AvatarTooltip label={getAssigneeName(task.assigned_to, task)} position="bottom">
                                  <div className={`w-6 h-6 ${colorFromSeed(String(task.assigned_to))} rounded-full flex items-center justify-center text-white text-xs font-medium`}>
                                    {getAssigneeInitials(task.assigned_to, task)}
                                  </div>
                                </AvatarTooltip>
                                <span className="text-sm text-gray-900">{getAssigneeName(task.assigned_to, task)}</span>
                              </>
                            ) : (
                              <>
                                <AvatarTooltip label="Unassigned" position="bottom">
                                  <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                                    <User className="w-4 h-4 text-gray-500" />
                                  </div>
                                </AvatarTooltip>
                                <span className="text-sm text-gray-500">Unassigned</span>
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{formatDateSafe((task as any).due_date)}</td>
                        <td className="px-4 py-3">{(task.labels || []).map((label: string, idx: number) => (<span key={idx} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full mr-1">{label}</span>))}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{formatDateSafe(task.created_at)}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{formatDateSafe(task.updated_at)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))
              ) : (
                currentPageTasks.map((task: any) => (
                  <tr key={task.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleTaskClick(task)}>
                    {/* <td className="px-4 py-3">
                      <input type="checkbox" checked={selectedTasks.has(task.id)} onChange={(e) => { e.stopPropagation(); handleTaskSelection(task.id, e.target.checked); }} className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500" />
                    </td> */}
                    <td className="px-4 py-3">{renderTaskType(task)}</td>
                    <td className="px-4 py-3"><div className="text-sm font-medium text-blue-600">{projectKey}-{task.display_number}</div></td>
                    <td className="px-4 py-3"><div className="text-sm text-gray-900">{task.title}</div></td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${getStatusColor(getTaskStatusName(task))}`}>
                        {String(getTaskStatusName(task)).toUpperCase()}
                      </span>
                    </td>
                    {/* <td className="px-4 py-3"><div className="flex items-center gap-1 text-sm text-gray-500"><MessageSquare className="w-4 h-4" /><span>{(task.comments || 0)} comment{(task.comments !== 1) ? 's' : ''}</span></div></td> */}
                    <td className="px-4 py-3 text-sm text-gray-500">{task.sprint_id ?? ''}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {task.assigned_to ? (
                          <>
                            <AvatarTooltip label={getAssigneeName(task.assigned_to, task)} position="bottom">
                              <div className={`w-6 h-6 ${colorFromSeed(String(task.assigned_to))} rounded-full flex items-center justify-center text-white text-xs font-medium`}>
                                {getAssigneeInitials(task.assigned_to, task)}
                              </div>
                            </AvatarTooltip>
                            <span className="text-sm text-gray-900">{getAssigneeName(task.assigned_to, task)}</span>
                          </>
                        ) : (
                          <>
                            <AvatarTooltip label="Unassigned" position="bottom">
                              <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                                <User className="w-4 h-4 text-gray-500" />
                              </div>
                            </AvatarTooltip>
                            <span className="text-sm text-gray-500">Unassigned</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{formatDateSafe((task as any).due_date)}</td>
                    <td className="px-4 py-3">{(task.labels || []).map((label: string, idx: number) => (<span key={idx} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full mr-1">{label}</span>))}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{formatDateSafe(task.created_at)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{formatDateSafe(task.updated_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Task Modal */}
      <TaskModal
        isOpen={isTaskModalOpen}
        onClose={handleTaskModalClose}
        task={currentTask || selectedTask}
        onStatusUpdate={handleStatusUpdate}
        workflowStatuses={workflowStatuses}
        onAddComment={async (taskId, html, parentId, plainText) => {
          const contentHtml = html;
          const contentText = plainText ?? toPlainText(ensureHtml(html || ''));
          const createdComment = await commentAPI.createComment({
            task_id: taskId,
            content: contentText,
            content_html: contentHtml,
            content_text: contentText,
            parent_comment_id: parentId
          });
          await loadComments(taskId);
          return createdComment;
        }}
        onUpdateDescription={async (taskId, description) => {
          await dispatch(updateTask({ id: taskId, data: { description } }));
          await dispatch(fetchTask(taskId));
        }}
        onUpdateAssignee={async (taskId, assigneeId) => {
          await dispatch(updateTask({ id: taskId, data: { assigned_to: assigneeId === null ? null : assigneeId } }));
          if (currentProject?.id) {
            dispatch(fetchTasks({ project_id: currentProject.id }));
            // Refresh project members after assignee update
            try {
              const data = await projectMemberAPI.getProjectMembers(currentProject.id);
              setProjectMembers(data || []);
            } catch (error) {
              console.error('Failed to refresh project members:', error);
            }
          }
          await dispatch(fetchTask(taskId));
        }}
        onUpdateReporter={async (taskId, reporterId) => {
          await dispatch(updateTask({ id: taskId, data: { reporter_id: reporterId === null ? null : reporterId } }));
          if (currentProject?.id) {
            dispatch(fetchTasks({ project_id: currentProject.id }));
            // Refresh project members after reporter update
            try {
              const data = await projectMemberAPI.getProjectMembers(currentProject.id);
              setProjectMembers(data || []);
            } catch (error) {
              console.error('Failed to refresh project members:', error);
            }
          }
          await dispatch(fetchTask(taskId));
        }}
        onUpdateFields={async (taskId, updates) => {
          // Don't call updateTask again - it's already been called by the component
          // Just refresh the task data
          if (currentProject?.id) {
            dispatch(fetchTasks({ project_id: currentProject.id }));
          }
          await dispatch(fetchTask(taskId));
          // Refresh project members if assignee was updated
          if (updates.assigned_to !== undefined && currentProject?.id) {
            try {
              const data = await projectMemberAPI.getProjectMembers(currentProject.id);
              setProjectMembers(data || []);
            } catch (error) {
              console.error('Failed to refresh project members:', error);
            }
          }
        }}
        activity={activity}
        taskDetails={selectedTask ? {
          assignee: selectedTask.assigned_to || 'Unassigned', labels: Array.isArray(selectedTask.labels)
            ? (selectedTask.labels as string[]).filter(l => typeof l === 'string' && l.trim()).map(l => l.trim())
            : (typeof (selectedTask as any).labels === 'string'
              ? (selectedTask as any).labels.split(',').map((label: string) => label.trim()).filter((label: string) => label)
              : []), sprint: selectedTask.sprint_id || 'Not set', dueDate: selectedTask.due_date || 'Not set'
        } : {}}
        projectMembers={persons}
        comments={comments}
        onDeleteComment={async (commentId) => { await deleteComment(commentId); if (selectedTask) await loadComments(selectedTask.id); }}
        onUpdateComment={async (commentId, html, plainText) => { await updateComment(commentId, html, plainText); if (selectedTask) await loadComments(selectedTask.id); }}
        projectKey={currentProject?.key || 'AHM'}
        project={currentProject}
        onUpdateTaskTitle={async (id, title) => { await dispatch(updateTask({ id, data: { title } })); dispatch(fetchTask(id)); }}
        onOpenSubtask={openSubtaskModal}
      />

      {/* Subtask Modal */}
      {activeSubtask && (
        <SubtaskModal
          subtask={activeSubtask}
          isOpen={isSubtaskModalOpen}
          onClose={closeSubtaskModal}
          parentKey={`${projectKey}-${(parentTaskForSubtask as any)?.display_number ?? (currentTask as any)?.display_number ?? (selectedTask as any)?.display_number ?? ''}`.replace(/-$/, '')}
          parentTitle={(parentTaskForSubtask || currentTask || selectedTask)?.title}
          parentTask={(parentTaskForSubtask || currentTask || selectedTask) as any}
          onOpenParent={(_parent) => {
            closeSubtaskModal();
            if (_parent) {
              handleTaskClick(_parent);
            }
          }}
          projectMembers={persons}
          activity={activity}
          projectKey={projectKey}
          project={currentProject}
          onAddComment={async (taskId, html, parentId, plainText) => {
            const contentHtml = html;
            const contentText = plainText ?? toPlainText(ensureHtml(html || ''));
            const createdComment = await commentAPI.createComment({
              task_id: taskId,
              content: contentText,
              content_html: contentHtml,
              content_text: contentText,
              parent_comment_id: parentId
            });
            if (parentTaskForSubtask || currentTask || selectedTask) await loadComments((parentTaskForSubtask || currentTask || selectedTask)?.id || '');
            return createdComment;
          }}
          onUpdateComment={async (commentId, html, plainText) => {
            await updateComment(commentId, html, plainText);
            if (parentTaskForSubtask || currentTask || selectedTask) await loadComments((parentTaskForSubtask || currentTask || selectedTask)?.id || '');
          }}
          onDeleteComment={async (commentId) => {
            await deleteComment(commentId);
            if (parentTaskForSubtask || currentTask || selectedTask) await loadComments((parentTaskForSubtask || currentTask || selectedTask)?.id || '');
          }}
          onSave={async (updates) => {
            await dispatch(updateSubtask({ subtaskId: activeSubtask.id, data: updates })).unwrap();
            // Refresh subtask data
            try {
              const fresh = await subtaskAPI.get(activeSubtask.id);
              setActiveSubtask(fresh);
            } catch (error) {
              console.error('Failed to refresh subtask:', error);
            }
            // Refresh project members after subtask update
            if (currentProject?.id) {
              try {
                const data = await projectMemberAPI.getProjectMembers(currentProject.id);
                setProjectMembers(data || []);
              } catch (error) {
                console.error('Failed to refresh project members:', error);
              }
            }
          }}
          onDelete={async () => {
            setIsSubtaskModalOpen(false);
          }}
        />
      )}

      {/* Footer */}
      <div className="flex items-center justify-end">
        <div className="flex items-center gap-3">
          <div className="text-sm text-gray-500">Showing {pageStart + 1}-{pageEnd} of {filteredTasks.length}</div>
          <div className="flex items-center gap-1">
            <button disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className={`px-2 py-1 rounded border text-sm ${page === 1 ? 'text-gray-300 border-gray-200' : 'text-gray-700 border-gray-300 hover:bg-gray-50'}`}>Prev</button>
            <span className="text-sm text-gray-600">Page {page} / {totalPages}</span>
            <button disabled={page === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className={`px-2 py-1 rounded border text-sm ${page === totalPages ? 'text-gray-300 border-gray-200' : 'text-gray-700 border-gray-300 hover:bg-gray-50'}`}>Next</button>
          </div>
        </div>
      </div>
    </div>
  );
};
